import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import Confetti from 'react-confetti';

import { WheelEntry } from '../../types/wheel';

interface SpinResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: WheelEntry | null;
  onConfirm?: () => void;
  confirmText?: string;
  spinAgainText?: string;
  onSpinAgain?: () => void;
}

const SpinResultModal: React.FC<SpinResultModalProps> = ({
  isOpen,
  onClose,
  result,
  onConfirm,
  confirmText = 'Confirm',
  spinAgainText = 'Spin Again',
  onSpinAgain,
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Update window size for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show confetti when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      // Stop confetti after 5 seconds
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle confirm action
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  // Handle spin again action
  const handleSpinAgain = () => {
    if (onSpinAgain) {
      onSpinAgain();
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          >
            {/* Confetti effect */}
            {showConfetti && (
              <Confetti
                width={windowSize.width}
                height={windowSize.height}
                recycle={false}
                numberOfPieces={200}
                colors={['#FF4EB8', '#A678FF', '#FF9A00', '#FFFFFF']} // SpinDom branding colors
              />
            )}

            {/* Modal */}
            <motion.div
              className="relative bg-gray-900 rounded-xl overflow-hidden max-w-md w-full mx-4"
              initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
              animate={{
                scale: 1,
                opacity: 1,
                rotate: 0,
                transition: {
                  type: 'spring',
                  damping: 15,
                  delay: 0.2,
                },
              }}
              exit={{ scale: 0.5, opacity: 0, rotate: 10 }}
              onClick={e => e.stopPropagation()}
            >
              {/* Animated border */}
              <div
                className="absolute inset-0 rounded-xl border-2 border-transparent bg-gradient-to-r from-spindom-pink via-spindom-purple to-spindom-orange animate-pulse"
                style={{ padding: '2px' }}
              >
                <div className="absolute inset-0 bg-gray-900 rounded-lg"></div>
              </div>

              {/* Close button with enhanced visibility */}
              <button
                onClick={e => {
                  e.stopPropagation();
                  onClose();
                }}
                className="absolute top-3 right-3 z-50 bg-spindom-pink rounded-full p-1.5 text-white hover:bg-pink-600 transition-colors shadow-lg"
                aria-label="Close"
                style={{ boxShadow: '0 0 10px rgba(255, 78, 184, 0.5)' }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Modal content */}
              <div className="p-8 relative z-10">
                {/* Result display with animation */}
                <motion.div
                  className="text-center mb-8"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1, transition: { delay: 0.5 } }}
                >
                  <h2 className="text-white text-2xl font-bold mb-6">Spin Result</h2>

                  <motion.div
                    className="text-4xl md:text-5xl font-bold text-center py-6 px-4 rounded-lg"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{
                      scale: 1,
                      opacity: 1,
                      transition: {
                        delay: 0.8,
                        type: 'spring',
                        stiffness: 300,
                      },
                    }}
                  >
                    <div className="text-transparent bg-clip-text bg-gradient-to-r from-spindom-pink via-spindom-purple to-spindom-orange animate-pulse-slow">
                      {result?.emoji && <span className="mr-2">{result.emoji}</span>}
                      {result?.text || 'No Result'}
                    </div>
                  </motion.div>
                </motion.div>

                {/* Action buttons */}
                <motion.div
                  className="flex flex-col space-y-3"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1, transition: { delay: 1 } }}
                >
                  <button
                    onClick={handleConfirm}
                    className="w-full py-3 px-4 rounded-lg font-bold text-white bg-gradient-to-r from-spindom-pink to-pink-600 hover:from-pink-600 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-spindom-pink/30"
                  >
                    {confirmText}
                  </button>

                  {onSpinAgain && (
                    <button
                      onClick={handleSpinAgain}
                      className="w-full py-3 px-4 rounded-lg font-bold text-white bg-gradient-to-r from-spindom-purple to-purple-600 hover:from-purple-600 hover:to-purple-700 transition-all duration-300"
                    >
                      {spinAgainText}
                    </button>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SpinResultModal;
